import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Send, Loader2, CornerDownLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { API } from "../lib/api";

export default function AskJoyn() {
  const { t, i18n } = useTranslation();
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asked, setAsked] = useState("");
  const [loading, setLoading] = useState(false);
  const answerRef = useRef(null);
  const suggestions = t("askjoyn.suggestions", { returnObjects: true });

  useEffect(() => {
    if (answerRef.current) answerRef.current.scrollTop = answerRef.current.scrollHeight;
  }, [answer]);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query || loading) return;
    setAsked(query); setQuestion(""); setAnswer(""); setLoading(true);
    try {
      const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
      const res = await fetch(`${API}/ask-joyn`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: query, lang }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) setAnswer((a) => a + evt.delta);
            if (evt.error) setAnswer((a) => a + evt.error);
          } catch { /* partial */ }
        }
      }
    } catch {
      setAnswer(t("askjoyn.error"));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="ask-joyn-widget" className="bg-white border border-border rounded-3xl overflow-hidden shadow-xl">
      <div className="h-1.5 joyn-gradient-bar" />
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ background: "linear-gradient(135deg,#4BB5E7,#3AAA35)" }}><Sparkles size={18} /></span>
          <div>
            <p className="font-display font-bold leading-none">{t("askjoyn.title")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("askjoyn.subtitle")}</p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2">
          <input data-testid="ask-joyn-input" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t("askjoyn.placeholder")}
            className="flex-1 border border-border rounded-full px-5 py-3 focus:outline-none focus:ring-2 focus:ring-joyn-cyan focus:border-transparent" disabled={loading} />
          <button data-testid="ask-joyn-send" type="submit" disabled={loading || !question.trim()}
            className="inline-flex items-center justify-center h-12 w-12 rounded-full text-white shrink-0 disabled:opacity-50 hover:-translate-y-0.5 transition-transform" style={{ background: "#4BB5E7" }} aria-label="Ask">
            {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>

        {!asked && (
          <div className="mt-4 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s} data-testid="ask-joyn-suggestion" onClick={() => ask(s)} disabled={loading}
                className="text-xs text-left bg-secondary hover:bg-secondary/70 border border-border rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-50">{s}</button>
            ))}
          </div>
        )}

        {asked && (
          <div className="mt-6">
            <div className="flex items-start gap-2 mb-4"><CornerDownLeft size={16} className="text-muted-foreground mt-1 shrink-0" /><p className="text-sm font-semibold">{asked}</p></div>
            <div ref={answerRef} data-testid="ask-joyn-answer" className="bg-secondary/60 border border-border rounded-2xl p-5 max-h-72 overflow-auto text-[15px] leading-relaxed">
              {answer ? (
                <div className="ask-joyn-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
                  {loading && <span className="inline-block w-1.5 h-4 align-middle ml-0.5 bg-joyn-cyan animate-pulse" />}
                </div>
              ) : (loading ? <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 size={15} className="animate-spin" /> {t("askjoyn.thinking")}</span> : null)}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">{t("askjoyn.disclaimer")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
